import { Injectable } from '@angular/core';
import { AppComponent } from 'src/app/app.component';
import { IoService } from '../io/io.service';
import { LocationService } from '../location/location.service';

// Every tag/label print in Quartz goes through here.
//
// Silent path: the request is submitted as a print job (printJob/submit). The
// server renders the same report/template the popup would show, one document
// per label, and queues it for the store's ISE desktop app that holds the Print
// Tags role; that app prints through its raw-ZPL Zebra path (identical output to
// the register's own tag prints) and reports back. Progress and the outcome show
// as snacks.
//
// Fallback path (sheet templates, no online tag printer at the location, no
// location picked, older API): the rendered document opens in a popup and the
// browser print dialog takes over, as before.
export interface TagPrintTemplate {
  key: string;
  name: string;
  label?: { widthIn: number; heightIn: number };
}

export interface TagPrintOutcome {
  silent: boolean;
  reason?: string;        // sheet | empty | offline | no-location | unsupported
  jobId?: string;
  labelCount?: number;
  terminalName?: string;
}

@Injectable({
  providedIn: 'root'
})
export class TagPrintService {

  private static readonly POLL_MS = 1500;
  private static readonly POLL_LIMIT_MS = 15 * 60 * 1000;

  constructor(
    private ioService: IoService,
    private locationService: LocationService,
  ) { }

  // endpoint: 'report/loadRecords' | 'report/printIseInventoryAccount' (leading slash tolerated)
  async print(endpoint: string, body: any, options: { description?: string, locationId?: string } = {}): Promise<TagPrintOutcome> {
    const path = String(endpoint || '').replace(/^\/+/, '');
    const locationId = options.locationId || this.activeLocationId();

    if (!locationId) {
      await this.printViaPopup(path, body);
      return { silent: false, reason: 'no-location' };
    }

    let submission: any;
    try {
      submission = await this.ioService.post('printJob/submit', {
        endpoint: path,
        body,
        locationId,
        description: options.description || null,
      });
    } catch (err) {
      // An API without the print-job module (404) keeps the old behaviour; a
      // real refusal (permission, bad template) is the caller's error.
      if (err && err.status === 404) {
        await this.printViaPopup(path, body);
        return { silent: false, reason: 'unsupported' };
      }
      throw err;
    }

    if (submission && submission.silent) {
      const count = submission.labelCount;
      AppComponent.showSnack(
        'Sent ' + count + (count === 1 ? ' tag' : ' tags') + ' to ' + submission.terminalName + '.', 'OK', 3000);
      this.watchJob(submission.jobId, submission.terminalName);
      return { silent: true, jobId: submission.jobId, labelCount: count, terminalName: submission.terminalName };
    }

    // Not queued: print the document the server already rendered.
    this.openRendered(submission && submission.rendered);
    return { silent: false, reason: submission && submission.reason };
  }

  // One inventory item's tag (Price Check), through the same routing.
  printInventoryItem(accountId: string, templateKey: string, locationId: string | null, description?: string): Promise<TagPrintOutcome> {
    return this.print('report/printIseInventoryAccount', {
      accountId,
      templateKey,
      locationId,
      omitPrintTrigger: true,
    }, { description, locationId: locationId || undefined });
  }

  private activeLocationId(): string | null {
    try {
      const location = this.locationService.getActiveLocation();
      return location && location._id ? location._id : null;
    } catch (e) {
      return null;
    }
  }

  private async printViaPopup(path: string, body: any) {
    const serverResponse: any = await this.ioService.post(path, Object.assign({}, body, { omitPrintTrigger: true }));
    this.openRendered(serverResponse && serverResponse.rendered);
  }

  // Popup + browser print dialog. Opened after the render, inside the user
  // activation window (a click is good for a few seconds), as ioService.print does.
  private openRendered(rendered: string) {
    if (!rendered) {
      AppComponent.showSnack('Nothing to print.', 'OK', 3000);
      return;
    }
    const printWindow = window.open('', '_blank', 'width=600,height=600');
    if (!printWindow) {
      AppComponent.showSnack('The print window was blocked. Allow popups for this site and try again.', 'OK', 5000);
      return;
    }
    printWindow.document.write(rendered);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      try { printWindow.print(); } catch (e) { }
    }, 1000);
  }

  // Follows a queued job to its end and tells the user how it went. On failure
  // the snack offers the browser dialog for the same labels.
  private watchJob(jobId: string, terminalName: string) {
    const started = Date.now();
    let lastReported = -1;
    const tick = async () => {
      if (Date.now() - started > TagPrintService.POLL_LIMIT_MS) { return; }
      let job: any;
      try {
        job = await this.ioService.post('printJob/status', { jobId });
      } catch (e) {
        return;
      }
      if (!job) { return; }
      if (job.status === 'done') {
        const tags = job.labelCount + (job.labelCount === 1 ? ' tag' : ' tags');
        AppComponent.showSnack('Printed ' + tags + ' on ' + terminalName + '.', 'OK', 4000);
        return;
      }
      if (job.status === 'failed' || job.status === 'canceled') {
        const printed = job.printedCount || 0;
        const message = 'Tag print on ' + terminalName + ' ' + (job.status === 'canceled' ? 'was canceled' : 'failed')
          + (printed > 0 ? ' after ' + printed + ' of ' + job.labelCount : '')
          + (job.error ? ': ' + job.error : '.');
        const snack = AppComponent.showSnack(message, 'Print here', 12000);
        snack.onAction().subscribe(async () => {
          try {
            const doc: any = await this.ioService.post('printJob/rendered', { jobId });
            this.openRendered(doc && doc.rendered);
          } catch (e) {
            AppComponent.showSnack('Unable to load the labels for printing.', 'OK', 4000);
          }
        });
        return;
      }
      if (job.status === 'printing' && job.labelCount > 25 && job.printedCount !== lastReported && job.printedCount > 0) {
        lastReported = job.printedCount;
      }
      setTimeout(tick, TagPrintService.POLL_MS);
    };
    setTimeout(tick, TagPrintService.POLL_MS);
  }
}
